import React, { useState } from 'react';
import './WorkshopContactSection.css';
import { Send, Palette, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { WORKSHOP_COUNTRY_CODES } from './workshopCountryCodes';

// Brevo list ids for the "What can we help you with?" question.
const CONTACT_OPTIONS = [
  { value: 9, title: 'Partnership', label: 'Partner with Not Junk Journaling to host a workshop through your church or organization.' },
  { value: 8, title: 'Resources Only', label: 'Download resources to begin your own bible journaling group.' },
  { value: 0, title: '', label: 'I do not want to host a workshop at this time.' },
];

const WORKSHOP_FORM_ACTION =
  'https://2ea12964.sibforms.com/serve/MUIFAESovNoQ7Sj4ndLAtocbJmctdT6gu2Bq1EV5qIftr8AZVjxyEjzXNeFbuBxrv3CSpdNcvR5rSu3F7cqwA8YPliG3-q6UeRn53B9Y47kecbpKxP6e1gx6cyRm6AiQ2FXm4EvjRLsREccuzSt4OtMxcpGU60CTVsR87L8QBGPjO4Tfe2yPb_Zh2dI8kwgI02YjmFjLPpRKGLre';

const WorkshopContactSection: React.FC = () => {
  const LOCALE = 'en';
  const GENERIC_INVALID_MESSAGE =
    'The information provided is invalid. Please review the field format and try again.';

  const usIndex = WORKSHOP_COUNTRY_CODES.findIndex((c) => c.iso === 'US');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryIndex, setCountryIndex] = useState(usIndex >= 0 ? usIndex : 0);
  const [phone, setPhone] = useState('');
  const [interest, setInterest] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [successActive, setSuccessActive] = useState(false);
  const [errorActive, setErrorActive] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [contactError, setContactError] = useState('');

  const validateEmail = (value: string) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value.trim());

  const selectInterest = (value: number) => {
    setInterest(value);
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorActive(false);
    setSuccessActive(false);
    setErrorText('');
    setContactError('');

    const form = e.currentTarget;

    // Honeypot: real users never fill this hidden field.
    const honeypot = form.querySelector<HTMLInputElement>('input[name="email_address_check"]');
    if (honeypot && honeypot.value) {
      setErrorActive(true);
      console.log('Honeypot triggered');
      setErrorText(GENERIC_INVALID_MESSAGE);
      return;
    }

    const emailProvided = email.trim().length > 0;
    const phoneProvided = phone.trim().length > 0;
    const country = WORKSHOP_COUNTRY_CODES[countryIndex];
    // Brevo expects the full international number as digits, e.g. 15551234567.
    const smsDigits = `${country?.code ?? ''}${phone}`.replace(/\D/g, '');

    let valid = true;

    // Email OR phone is required — not both.
    if (!emailProvided) {
      setContactError('Please enter an email address so we can reach you.');
      valid = false;
    } else {
      if (emailProvided && !validateEmail(email)) {
        setContactError('Please enter a valid email address.');
        valid = false;
      }
      if (phoneProvided && (smsDigits.length < 6 || smsDigits.length > 19)) {
        setContactError('Please enter a valid phone number, including your area code.');
        valid = false;
      }
    }

    if (!valid) return;

    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.set('FIRSTNAME', firstName.trim());
      formData.set('LASTNAME', lastName.trim());
      if (emailProvided) formData.set('EMAIL', email.trim());
      if (phoneProvided) formData.set('SMS', smsDigits);
      if (interest !== null) formData.append('lists_32[]', String(interest));
      formData.set('locale', LOCALE);
      formData.set('html_type', 'simple');

      const res = await fetch(`${WORKSHOP_FORM_ACTION}?isAjax=1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const data = await res.json();
      if (data.success) {
        if (data.redirect) {
          window.location.replace(data.redirect);
          return;
        }
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setInterest(0);
        form.reset();
        setSuccessActive(true);
      } else {
        setErrorActive(true);
        console.log('Form submission error:', data);
        setErrorText(data.message || GENERIC_INVALID_MESSAGE);
      }
    } catch {
      setErrorActive(true);
      console.log('Form submission failed due to an exception.', e);
      setErrorText(GENERIC_INVALID_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const inputClasses =
    'w-full px-5 py-4 border border-gray-200 rounded-full font-sans focus:border-[#D4A5A5] focus:ring-[#D4A5A5] focus:outline-none';

  return (
    <section className="bg-[#FDF8F5]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl shadow-xl p-8 sm:p-12 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4A5A5]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#9CAF88]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

          <div className="relative z-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#C9A962]/20 rounded-full mb-6">
              <Palette className="w-8 h-8 text-[#C9A962]" />
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#3D3D3D] mb-4">
              Get Notified
            </h2>

            <p className="font-sans text-[#3D3D3D]/70 max-w-lg mx-auto mb-8">
              Stay up to date on upcoming workshops and notifications.
            </p>

            <div
              id="error-message"
              className={`sib-form-message-panel ${errorActive ? 'sib-form-message-panel--active' : ''}`}
            >
              <div className="sib-form-message-panel__text sib-form-message-panel__text--center">
                <span className="sib-form-message-panel__inner-text">
                  {errorText || 'Your request could not be saved. Please try again.'}
                </span>
              </div>
            </div>

            {successActive ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-center gap-3 text-[#9CAF88]"
              >
                <Check className="w-6 h-6" />
                <span className="font-sans text-lg font-medium">
                  Thank you! We&apos;ll be in touch about workshops soon.
                </span>
              </motion.div>
            ) : (
              <form
                method="POST"
                action={WORKSHOP_FORM_ACTION}
                onSubmit={onSubmit}
                className="max-w-xl mx-auto text-left"
              >
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      className={inputClasses}
                      maxLength={200}
                      type="text"
                      id="FIRSTNAME"
                      name="FIRSTNAME"
                      autoComplete="given-name"
                      placeholder="First name"
                      required
                      value={firstName}
                      onChange={(ev) => setFirstName(ev.target.value)}
                    />
                    <input
                      className={inputClasses}
                      maxLength={200}
                      type="text"
                      id="LASTNAME"
                      name="LASTNAME"
                      autoComplete="family-name"
                      placeholder="Last name"
                      required
                      value={lastName}
                      onChange={(ev) => setLastName(ev.target.value)}
                    />
                  </div>

                  <input
                    className={inputClasses}
                    type="email"
                    id="EMAIL"
                    name="EMAIL"
                    autoComplete="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                  />

                  <div className="flex gap-3">
                    <select
                      className="w-28 shrink-0 px-3 py-4 border border-gray-200 rounded-full font-sans bg-white focus:border-[#D4A5A5] focus:ring-[#D4A5A5] focus:outline-none"
                      value={countryIndex}
                      onChange={(ev) => setCountryIndex(Number(ev.target.value))}
                      aria-label="Phone country code"
                    >
                      {WORKSHOP_COUNTRY_CODES.map((c, i) => (
                        <option key={i} value={i}>
                          {c.code} {c.iso}
                        </option>
                      ))}
                    </select>
                    <input
                      className="flex-1 px-5 py-4 border border-gray-200 rounded-full font-sans focus:border-[#D4A5A5] focus:ring-[#D4A5A5] focus:outline-none"
                      type="tel"
                      id="SMS"
                      name="SMS"
                      autoComplete="tel"
                      placeholder="Phone number"
                      value={phone}
                      onChange={(ev) => setPhone(ev.target.value)}
                    />
                  </div>

                  {contactError && (
                    <p className="font-sans text-sm text-red-600">{contactError}</p>
                  )}
                </div>

                <div className="my-6 border-t border-gray-100" />

                <fieldset>
                  <legend className="font-sans font-semibold text-[#3D3D3D]">
                    Want to host your own workshop?
                  </legend>
                  <div className="font-sans text-[#3D3D3D]/80 transition-colors mb-3">
                    Select an option below, and we will contact you about how you can get started.
                  </div>
                  <div className="space-y-3">
                    {CONTACT_OPTIONS.map((opt) => {
                      const selected = interest === opt.value;
                      return (
                        <label
                          key={opt.value}
                          className={`flex items-start gap-3 cursor-pointer rounded-2xl border p-4 transition-colors ${
                            selected
                              ? 'border-[#9CAF88] bg-[#9CAF88]/10'
                              : 'border-gray-200 hover:border-[#9CAF88]/60'
                          }`}
                        >
                          <input
                            type="checkbox"
                            name="lists_32[]"
                            value={opt.value}
                            className="mt-1 w-5 h-5 rounded-full border-gray-300 accent-[#9CAF88] focus:ring-[#9CAF88]"
                            checked={selected}
                            onChange={() => selectInterest(opt.value)}
                          />
                          <span className="flex flex-col">
                            <span className="font-sans font-semibold text-[#3D3D3D]">
                              {opt.title}
                            </span>
                            <span className="font-sans text-sm text-[#3D3D3D]/70">
                              {opt.label}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                <button
                  className="w-full sm:w-auto mt-6 bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white px-8 py-4 font-sans rounded-full inline-flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      Submit
                      <Send className="ml-2 w-4 h-4" />
                    </>
                  )}
                </button>

                <input
                  title="Leave this field empty"
                  type="text"
                  name="email_address_check"
                  defaultValue=""
                  className="input--hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />
                <input type="hidden" name="locale" value="en" />
                <input type="hidden" name="html_type" value="simple" />
              </form>
            )}

            <p className="font-sans text-sm text-[#3D3D3D]/50 mt-6">
              We&apos;ll only use this to share workshop details. We never sell your personal
              information.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkshopContactSection;
